import styles from "./DraggableNodeViewWrapper.module.css"

import { NodeViewWrapper, type ReactNodeViewProps } from "@tiptap/react"
import type { HTMLAttributes, ReactNode, Ref } from "react"
import { DragHandle } from "./DragHandle"

interface DraggableNodeViewWrapperProps extends ReactNodeViewProps {
  /**
   * Class name for the outer NodeViewWrapper.
   *
   * By default, it is the module class from DraggableNodeViewWrapper.module.css,
   * which shows the drag handle whenever the node or an area 24px to the left is hovered.
   * If you use a different class, you must supply your own hover rule,
   * setting `.draggable-node-view-drag-handle { opacity: 1; }` when desired.
   */
  wrapperClassName?: string
  /**
   * Class name applied to the drag handle itself. Use it to override the handle's
   * geometry by declaring --drag-handle-* CSS vars (defaults in DragHandle.module.css),
   * e.g. `.myDragHandle { --drag-handle-top: 2px; }`. Declaring the vars directly on the
   * handle (rather than on an ancestor) keeps them from inheriting into the drag handles
   * of node views nested inside this one.
   */
  handleClassName?: string
  /**
   * Extra props (and a ref) for the outer NodeViewWrapper element — e.g. hover-chrome
   * tracking (useHoverChrome) and its data-chrome-visible attribute.
   */
  wrapperProps?: HTMLAttributes<HTMLElement> & {
    ref?: Ref<HTMLElement>
    [dataAttribute: `data-${string}`]: unknown
  }
  children: ReactNode
}

/**
 * Use in place of NodeViewWrapper to also add a drag handle to the node
 * (sized for blocks/widgets).
 *
 * You must also add `draggable: true` to the extension spec.
 * (This is used for ProseMirror's internal drag-and-drop tracking; it's distinct from
 * setting draggable="true" on the drag handle itself.)
 */
export function DraggableNodeViewWrapper({
  editor,
  getPos,
  wrapperClassName = styles.draggableNodeViewWrapper,
  handleClassName,
  wrapperProps,
  children,
}: DraggableNodeViewWrapperProps) {
  return (
    <NodeViewWrapper
      className={wrapperClassName}
      style={{ position: "relative" }}
      {...wrapperProps}
    >
      {editor.isEditable && (
        <DragHandle editor={editor} getPos={getPos} className={handleClassName} />
      )}
      {children}
    </NodeViewWrapper>
  )
}
